import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import type { ContactDetail, Deal } from './types';
import { initials, displayName } from './convUtils';
import PersonaChip from './PersonaChip';

const money = (n: number | null | undefined) =>
  n == null ? '—' : Number(n).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

const fmtDate = (iso: string | null) => {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

export default function ContactContextPanel({
  contact,
  loading,
  conversationId,
  onSelectChannel,
  onOpenDeal,
  onClose,
}: {
  contact: ContactDetail | null;
  loading: boolean;
  conversationId?: string | null;
  onSelectChannel?: (channel: 'sms' | 'email') => void;
  onOpenDeal?: (id: string) => void;
  onClose?: () => void;
}) {
  const [deals, setDeals] = useState<Deal[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState('');
  const [suggested, setSuggested] = useState<string[]>([]);
  const [suggesting, setSuggesting] = useState(false);
  const [notes, setNotes] = useState<Array<{ id: string; body: string; dateAdded?: string }>>([]);
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const ghlId = contact?.ghl_id || null;

  useEffect(() => {
    setTags(contact?.tags || []);
    setSuggested([]);
  }, [contact?.ghl_id]);

  useEffect(() => {
    if (!ghlId) {
      setDeals([]);
      setNotes([]);
      return;
    }
    let alive = true;
    api.getContactDeals(ghlId).then((r) => alive && setDeals(r.deals || [])).catch(() => alive && setDeals([]));
    api.getContactNotes(ghlId).then((r) => alive && setNotes(r.notes || [])).catch(() => alive && setNotes([]));
    return () => {
      alive = false;
    };
  }, [ghlId]);

  const addTag = async (raw: string) => {
    const t = raw.trim();
    if (!t || !ghlId || tags.some((x) => x.toLowerCase() === t.toLowerCase())) return;
    const prev = tags;
    setTags([...tags, t]);
    setSuggested((s) => s.filter((x) => x.toLowerCase() !== t.toLowerCase()));
    try {
      const r = await api.setContactTags(ghlId, { add: [t] });
      setTags(r.tags || [...prev, t]);
    } catch {
      setTags(prev);
    }
  };

  const removeTag = async (t: string) => {
    if (!ghlId) return;
    const prev = tags;
    setTags(tags.filter((x) => x !== t));
    try {
      const r = await api.setContactTags(ghlId, { remove: [t] });
      setTags(r.tags ?? prev.filter((x) => x !== t));
    } catch {
      setTags(prev);
    }
  };

  const runSuggest = async () => {
    if (!conversationId) return;
    setSuggesting(true);
    try {
      const r = await api.suggestConversationTags(conversationId);
      const have = new Set(tags.map((x) => x.toLowerCase()));
      setSuggested((r.suggestions || []).filter((s: string) => !have.has(s.toLowerCase())));
    } catch {
      setSuggested([]);
    } finally {
      setSuggesting(false);
    }
  };

  const addNote = async () => {
    const body = newNote.trim();
    if (!body || !ghlId) return;
    setAddingNote(true);
    try {
      await api.addContactNote(ghlId, body);
      setNotes((n) => [{ id: `tmp-${Date.now()}`, body }, ...n]);
      setNewNote('');
    } catch {
      /* keep the draft */
    } finally {
      setAddingNote(false);
    }
  };

  if (!contact) {
    return (
      <div className="p-6 text-center text-sm text-gray-400">
        {loading ? 'Loading contact…' : 'No contact selected'}
      </div>
    );
  }

  const name = displayName(contact.name || contact.phone);
  const section = 'px-4 py-3 border-b border-gray-100';
  const label = 'text-[10px] uppercase tracking-wide text-gray-400 mb-1.5';
  const addr = [contact.address.line1, contact.address.city, contact.address.state, contact.address.postal]
    .filter(Boolean)
    .join(', ');
  const dc = contact.dnd_channels || { sms: false, email: false, call: false };
  const anyDnd = contact.dnd || dc.sms || dc.email || dc.call;
  const birthday = (() => {
    if (!contact.date_of_birth) return null;
    const d = new Date(contact.date_of_birth);
    return isNaN(d.getTime()) ? null : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  })();

  const actionBtn = (active: boolean) =>
    `flex-1 flex items-center justify-center gap-1 text-[11px] py-1.5 rounded-md font-medium transition-colors ${
      active ? 'border border-gray-300 text-gray-700 hover:bg-gray-50' : 'border border-gray-200 text-gray-300 cursor-not-allowed'
    }`;

  return (
    <div className="text-sm">
      {onClose && (
        <div className="flex justify-end px-3 pt-3">
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-700" aria-label="Close">✕</button>
        </div>
      )}

      {/* Identity */}
      <div className="px-4 py-4 flex flex-col items-center text-center gap-2 border-b border-gray-100">
        {contact.profile_photo ? (
          <img src={contact.profile_photo} alt="" className="w-12 h-12 rounded-full object-cover" />
        ) : (
          <div className="w-12 h-12 rounded-full bg-primary-100 text-primary-500 flex items-center justify-center text-base font-semibold">
            {initials(name)}
          </div>
        )}
        <div>
          <div className="font-semibold text-gray-900">{name}</div>
          {contact.company && <div className="text-xs text-gray-500">{contact.company}</div>}
        </div>
        {contact.persona.label && (
          <PersonaChip label={contact.persona.label} role={contact.persona.role} status={contact.persona.status} />
        )}
        {anyDnd && (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-warning-wash text-warning font-medium">
            ⚠ Do-not-disturb
          </span>
        )}
      </div>

      {/* Action bar */}
      <div className="flex gap-1.5 px-3 py-2.5 border-b border-gray-100">
        {contact.ghl_contact_url ? (
          <a href={contact.ghl_contact_url} target="_blank" rel="noreferrer" className={actionBtn(true)}>
            Call in GHL
          </a>
        ) : (
          <span className={actionBtn(false)}>Call in GHL</span>
        )}
        <button type="button" disabled={!contact.phone} onClick={() => onSelectChannel?.('sms')} className={actionBtn(!!contact.phone)}>
          Text
        </button>
        <button type="button" disabled={!contact.email} onClick={() => onSelectChannel?.('email')} className={actionBtn(!!contact.email)}>
          Email
        </button>
      </div>

      {/* Per-channel DND */}
      {anyDnd && (
        <div className={section}>
          <div className={label}>Do-not-disturb</div>
          <div className="flex flex-wrap gap-1.5">
            {(['sms', 'email', 'call'] as const).map((ch) => (
              <span
                key={ch}
                className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                  dc[ch] ? 'bg-red-50 text-red-700' : 'bg-success-wash text-success'
                }`}
              >
                {ch.toUpperCase()} {dc[ch] ? 'blocked' : 'OK'}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Intent */}
      {contact.persona.intent && (
        <div className={section}>
          <div className={label}>Intent</div>
          <p className="text-xs text-gray-600 leading-relaxed">{contact.persona.intent}</p>
        </div>
      )}

      {/* Contact fields */}
      <div className={section}>
        <div className={label}>Contact</div>
        <div className="space-y-1.5 text-xs">
          {contact.phone && (
            <Row k="Phone">
              {/* PHONE1: selectable TEXT, never a tel: link - on an iPhone a tel: call dials his personal line and
                  GoHighLevel never records it. Call in GHL is above. */}
              <span className="text-gray-700 select-all">{contact.phone}</span>
            </Row>
          )}
          {contact.email && (
            <Row k="Email"><a href={`mailto:${contact.email}`} className="text-info hover:underline break-all">{contact.email}</a></Row>
          )}
          {/* LF1: strings only, whatever the type says - GoHighLevel answers extra emails as objects, and one object
              rendered here blanked the whole Conversations page. The server normalises them; this never trusts it. */}
          {(contact.additional_emails as unknown[]).filter((e): e is string => typeof e === 'string').map((e) => (
            <Row k="Alt email" key={e}><a href={`mailto:${e}`} className="text-info hover:underline break-all">{e}</a></Row>
          ))}
          {addr && <Row k="Address"><span className="text-right">{addr}</span></Row>}
          {contact.website && (
            <Row k="Website">
              <a href={contact.website.startsWith('http') ? contact.website : `https://${contact.website}`} target="_blank" rel="noreferrer" className="text-info hover:underline break-all">
                {contact.website}
              </a>
            </Row>
          )}
          {contact.owner && <Row k="Owner"><span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: contact.owner.color }} />{contact.owner.name}</span></Row>}
          {contact.source && <Row k="Source">{contact.source}</Row>}
          {contact.address.country && <Row k="Country">{contact.address.country}</Row>}
          {contact.timezone && <Row k="Timezone">{contact.timezone}</Row>}
          {birthday && <Row k="Birthday">{birthday}</Row>}
          {fmtDate(contact.date_added) && <Row k="Added">{fmtDate(contact.date_added)}</Row>}
          {!contact.phone && !contact.email && <div className="text-gray-400">No contact details</div>}
        </div>
      </div>

      {/* Deals */}
      <div className={section}>
        <div className={label}>Deals{deals.length ? ` (${deals.length})` : ''}</div>
        {deals.length === 0 ? (
          <div className="text-xs text-gray-400">No linked deals</div>
        ) : (
          <div className="space-y-1.5">
            {deals.map((d) => (
              <button
                key={d.id || d.ghl_id}
                type="button"
                onClick={() => onOpenDeal?.(d.id)}
                className="block w-full text-left border border-gray-200 rounded-lg px-2.5 py-1.5 hover:border-primary-300 hover:bg-gray-50 transition-colors"
              >
                <div className="text-xs font-medium text-gray-900 truncate">{d.name || 'Untitled deal'} →</div>
                <div className="flex items-center justify-between text-[11px] text-gray-500 mt-0.5">
                  <span className="capitalize">{d.status || '-'}</span>
                  <span>{money(d.monetary_value)}</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Tags (editable + AI suggest) */}
      <div className={section}>
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[10px] uppercase tracking-wide text-gray-400">Tags</span>
          {conversationId && (
            <button type="button" onClick={runSuggest} disabled={suggesting} className="text-[10px] text-info hover:underline disabled:opacity-50">
              {suggesting ? 'Thinking…' : '✦ Suggest'}
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-1 mb-1.5">
          {tags.length === 0 && <span className="text-xs text-gray-400">No tags</span>}
          {tags.map((t) => (
            <span key={t} className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
              {t}
              <button type="button" onClick={() => removeTag(t)} className="text-gray-400 hover:text-red-600" aria-label={`Remove ${t}`}>×</button>
            </span>
          ))}
        </div>
        {suggested.length > 0 && (
          <div className="mb-1.5">
            <div className="text-[10px] text-gray-400 mb-1">Suggested (click to add):</div>
            <div className="flex flex-wrap gap-1">
              {suggested.map((s) => (
                <button key={s} type="button" onClick={() => addTag(s)} className="text-[10px] px-1.5 py-0.5 rounded border border-info/30 text-info hover:bg-info-wash">+ {s}</button>
              ))}
            </div>
          </div>
        )}
        <input
          value={newTag}
          onChange={(e) => setNewTag(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(newTag); setNewTag(''); } }}
          placeholder="Add a tag, press Enter"
          className="w-full text-xs border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
        />
      </div>

      {/* Notes */}
      <div className={section}>
        <div className={label}>Notes</div>
        <div className="space-y-1.5 mb-2 max-h-40 overflow-y-auto">
          {notes.length === 0 ? (
            <div className="text-xs text-gray-400">No notes</div>
          ) : (
            notes.map((n) => (
              <div key={n.id} className="text-xs text-gray-700 bg-gray-50 rounded px-2 py-1.5 whitespace-pre-wrap break-words">
                {stripHtml(n.body)}
              </div>
            ))
          )}
        </div>
        <div className="flex items-end gap-1.5">
          <textarea
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            rows={2}
            placeholder="Add a note…"
            className="flex-1 resize-none text-xs border border-gray-300 rounded px-2 py-1 outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
          <button type="button" onClick={addNote} disabled={addingNote || !newNote.trim()} className="text-xs px-2.5 py-1.5 bg-primary-500 text-white rounded disabled:opacity-50">
            {addingNote ? '…' : 'Add'}
          </button>
        </div>
      </div>

      {/* Links */}
      {contact.apex_contact_id && (
        <div className="px-4 py-3">
          <Link to={`/crm/contacts/${contact.apex_contact_id}`} className="block text-center text-xs text-primary-500 border border-gray-300 rounded-lg py-2 hover:bg-gray-50">
            View full contact →
          </Link>
        </div>
      )}
    </div>
  );
}

function stripHtml(s: string): string {
  return (s || '').replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
}

function Row({ k, children }: { k: string; children: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-gray-400 shrink-0">{k}</span>
      <span className="text-gray-700 text-right min-w-0">{children}</span>
    </div>
  );
}
